/**
 * GlobalLoginModal - Modal de login/inscription global accessible depuis toutes les pages
 * Utilise le LoginContext pour gérer l'état d'ouverture
 *
 * Modes:
 * - "login": Connexion avec lien magique ou mot de passe
 * - "register": Inscription gratuite avec email + password + password_confirm
 *
 * Après succès: exécute pendingAction si présent (ex: relancer export PDF)
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { useToast } from "../hooks/use-toast";
import { useLogin } from "../contexts/LoginContext";
import { useSelection } from "../contexts/SelectionContext";
import { LogIn, Mail, KeyRound, Loader2, UserPlus, Crown, ArrowLeft, CheckCircle } from "lucide-react";
import { getApiErrorMessage } from "../lib/errorMessage";
import { useBeta } from "../hooks/useBeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* UNE SEULE FORMULATION, ET ELLE NE PROMET RIEN QU'ON NE TIENNE.
 *
 * Deux textes se contredisaient : le serveur répondait « si un compte Pro
 * existe… », le toast « si un compte existe… ». Le premier révélait une
 * condition tout en prétendant rester neutre ; le second la niait. Celle-ci
 * ne dit ni si le compte existe, ni s'il est Pro — elle dit ce qui est vrai :
 * un lien part si cette adresse peut en recevoir un. */
export const MESSAGE_LIEN_NEUTRE =
  "Si un lien de connexion peut être envoyé à cette adresse, il vient de partir.";

// Doit rester aligné avec la validation backend (register-free / set-password) :
// le serveur refuse tout mot de passe de moins de 8 caractères.
const MIN_PASSWORD_LENGTH = 8;

/**
 * L'information RGPD, au moment où l'adresse est saisie.
 *
 * POURQUOI ICI, ET PAS SEULEMENT DANS UNE PAGE.
 *
 * L'article 13 demande que l'information soit FOURNIE à la personne concernée
 * « au moment où les données sont obtenues ». Un lien en pied de page est
 * disponible ; il n'est pas fourni. On l'affiche donc à l'écran où l'adresse
 * est tapée, avant la première requête qui l'envoie au serveur.
 *
 * AVANT LAQUELLE, EXACTEMENT. Pour le parcours par code, c'est
 * `POST /auth/request-code` — à l'étape E-MAIL, pas à l'étape du code :
 * l'adresse part dès la première soumission, et informer à la validation du
 * code serait informer après la collecte. Pour le parcours par mot de passe,
 * c'est `POST /auth/register-free`.
 *
 * Information de premier niveau, volontairement courte (art. 12 : concise,
 * transparente, intelligible). Le détail — finalités, bases légales, durées,
 * sous-traitants, droits, et l'identité du responsable du traitement — vit
 * dans la politique de confidentialité, vers laquelle elle renvoie.
 */
const InfoConfidentialite = () => (
  <p className="rounded-md bg-muted/60 px-3 py-2 text-[11.5px] leading-relaxed text-muted-foreground">
    Votre adresse sert à vous connecter et à vous envoyer les messages du
    service. Elle n'est ni revendue, ni cédée. Vous pouvez y accéder, la
    corriger ou la supprimer à tout moment.{" "}
    <a
      href="/politique-confidentialite"
      target="_blank"
      rel="noreferrer"
      className="font-medium underline underline-offset-2 hover:text-foreground"
    >
      Politique de confidentialité et responsable du traitement
    </a>
  </p>
);

function GlobalLoginModal() {
  const navigate = useNavigate();
  // En tête du composant : le libellé du lien vers `/pricing` dépend de la
  // bêta, et un hook ne s'appelle jamais depuis un fragment de rendu.
  const beta = useBeta();
  const { toast } = useToast();
  const {
    showLoginModal,
    setShowLoginModal,
    closeLogin,
    initialMode,
    setInitialMode,
    executePendingAction
  } = useLogin();
  const { selectedExercises, importSelectionToAccount } = useSelection();

  // Mode principal: "login" ou "register"
  const [mode, setMode] = useState(initialMode);

  // États du formulaire login
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  /* LE CODE EST LE PARCOURS PRINCIPAL.
   *
   * Il ne quitte pas la fenêtre : c'est sa vertu première. Le lien magique
   * ouvre une route de vérification et remonte toute l'application — ce qui a
   * fait perdre l'intention d'enregistrer, puis celle d'exporter. Un code se
   * saisit ici, donc il n'y a rien à traverser et rien à rattraper. */
  //: Quel écran d'authentification est montré. Le code par défaut ; le mot de
  //: passe seulement si on le demande explicitement.
  const [ecranAuth, setEcranAuth] = useState("code"); // "code" | "motdepasse"

  //: Le parcours par code a deux temps : on demande, puis on saisit.
  const [etapeCode, setEtapeCode] = useState("email"); // "email" | "code"
  const [codeEmail, setCodeEmail] = useState("");
  const [codeSaisi, setCodeSaisi] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeErreur, setCodeErreur] = useState(null);
  const [loginEmailSent, setLoginEmailSent] = useState(false);
  const [loginMagicError, setLoginMagicError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);

  // États du formulaire register
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState("");
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState(null);

  // P0: État pour afficher le lien magique en mode dev
  const [devMagicLink, setDevMagicLink] = useState(null);

  // Mot de passe oublié — l'ancien bouton naviguait vers /reset-password SANS
  // token (page « lien invalide » garantie). On déclenche ici la vraie demande.
  const [resetRequestLoading, setResetRequestLoading] = useState(false);
  const [resetRequestMessage, setResetRequestMessage] = useState(null);
  const [devResetLink, setDevResetLink] = useState(null);

  const handleResetPasswordRequest = async () => {
    if (!loginEmail) {
      setResetRequestMessage("Saisissez d'abord votre adresse email ci-dessus.");
      return;
    }
    setResetRequestLoading(true);
    setResetRequestMessage(null);
    setDevResetLink(null);
    try {
      const response = await axios.post(`${API}/auth/reset-password-request`, {
        email: loginEmail,
      });
      setResetRequestMessage(
        "Si un compte existe pour cette adresse, un lien de réinitialisation a été envoyé par email."
      );
      if (response.data?.dev_mode && response.data?.reset_link) {
        setDevResetLink(response.data.reset_link);
      }
    } catch (e) {
      setResetRequestMessage("La demande a échoué. Réessayez dans un instant.");
    } finally {
      setResetRequestLoading(false);
    }
  };

  // Sync mode with initialMode when modal opens
  useEffect(() => {
    if (showLoginModal) {
      setMode(initialMode);
    }
  }, [showLoginModal, initialMode]);

  // Reset form when modal closes
  useEffect(() => {
    if (!showLoginModal) {
      setLoginEmail("");
      setLoginPassword("");
      setLoginEmailSent(false);
      setLoginLoading(false);
      setEcranAuth("code");
      setEtapeCode("email");
      setCodeEmail("");
      setCodeSaisi("");
      setCodeErreur(null);
      setDevMagicLink(null);
      setRegisterEmail("");
      setRegisterPassword("");
      setRegisterPasswordConfirm("");
      setRegisterLoading(false);
      setRegisterError(null);
    }
  }, [showLoginModal]);

  const generateDeviceId = () => {
    return 'device_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  };

  /**
   * Callback après login/register réussi
   * - Stocke le session token
   * - Dispatch event auth-changed
   * - Ferme le modal
   * - Exécute pendingAction si présent
   * - Importe la sélection vers la bibliothèque utilisateur si non vide
   */
  const onAuthSuccess = (sessionToken, email, isPro = false, compteCree = false,
                         garderOuverte = false) => {
    // Store session token and user info
    localStorage.setItem('lemaitremot_session_token', sessionToken);
    localStorage.setItem('lemaitremot_user_email', email);
    localStorage.setItem('lemaitremot_login_method', 'session');

    // Dispatcher l'événement pour notifier les composants utilisant useAuth()
    window.dispatchEvent(new Event('lmm:auth-changed'));

    if (!garderOuverte) closeLogin();

    /* Le message disait « Compte créé ! » à quiconque se connectait, même
     * depuis dix ans. On distingue enfin les deux cas — c'est le backend qui
     * tranche, via `compte_cree`. */
    /* La création s'annonce DANS la modale, pas par un message flottant.
     *
     * Mesuré : le toast « Votre compte gratuit est créé » était évincé dans la
     * milliseconde par ceux de l'action reprise et de l'import de sélection.
     * Un message qu'on ne voit pas ne vaut rien, et celui-ci est le seul de la
     * séance à dire qu'un compte vient d'exister. */
    if (compteCree) {
      // rien ici : l'écran de confirmation s'en charge
    } else {
      toast({
        title: isPro ? "Connexion Pro réussie" : "Connexion réussie",
        description: isPro
          ? "Vous êtes connecté à votre compte Pro."
          : "Vous êtes connecté.",
      });
    }

    // Exécuter l'action pending si présente
    const pending = executePendingAction();
    if (pending) {
      console.log('🔄 Exécution de l\'action pending:', pending);
      // L'action sera exécutée par le composant qui l'a définie
      // via un event custom ou un callback
      window.dispatchEvent(new CustomEvent('lmm:execute-pending-action', { detail: pending }));
    }

    // P3.1: Importer la sélection vers la bibliothèque utilisateur si non vide
    // Éviter double import avec un flag basé sur le token
    const tokenImportFlag = `selection_imported_for_token:${sessionToken}`;
    const hasImported = sessionStorage.getItem(tokenImportFlag);

    if (!hasImported && selectedExercises.length > 0) {
      // Marquer que l'import va être effectué pour ce token
      sessionStorage.setItem(tokenImportFlag, 'true');

      // Importer la sélection en arrière-plan
      importSelectionToAccount(sessionToken)
        .then(result => {
          if (result.imported > 0) {
            console.log(`✅ ${result.imported} exercice(s) importé(s) dans la bibliothèque`);
            toast({
              title: "Sélection importée",
              description: `${result.imported} exercice(s) ajouté(s) à votre bibliothèque`,
              action: (
                <Button
                  onClick={() => navigate('/mes-exercices')}
                  variant="outline"
                  size="sm"
                  className="h-8"
                >
                  Voir mes exercices
                </Button>
              )
            });
          } else if (result.skipped > 0) {
            console.log(`ℹ️ ${result.skipped} exercice(s) déjà présents dans la bibliothèque`);
          }

          if (result.errors && result.errors.length > 0) {
            console.warn(`⚠️ ${result.errors.length} exercice(s) n'ont pas pu être importés`, result.errors);
          }
        })
        .catch(error => {
          console.error("Erreur lors de l'import de la sélection:", error);
          // Réinitialiser le flag pour permettre un nouvel essai
          sessionStorage.removeItem(tokenImportFlag);
          toast({
            title: "Erreur lors de l'import",
            description: "Certains exercices n'ont pas pu être importés dans votre bibliothèque",
            variant: "destructive"
          });
        });
    }

    // Rediriger vers returnTo si présent (et pas de pending action)
    if (!pending) {
      const returnTo = sessionStorage.getItem('postLoginRedirect');
      if (returnTo) {
        sessionStorage.removeItem('postLoginRedirect');
        setTimeout(() => {
          navigate(returnTo);
        }, 100);
      }
    }
  };

  // =====================================================
  // REGISTER FREE
  // =====================================================
  const handleRegisterFree = async () => {
    if (!registerEmail || !registerPassword || !registerPasswordConfirm) {
      setRegisterError("Veuillez remplir tous les champs");
      return;
    }

    if (registerPassword !== registerPasswordConfirm) {
      setRegisterError("Les mots de passe ne correspondent pas");
      return;
    }

    if (registerPassword.length < MIN_PASSWORD_LENGTH) {
      setRegisterError(`Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères`);
      return;
    }

    setRegisterLoading(true);
    setRegisterError(null);

    try {
      const deviceId = localStorage.getItem('lemaitremot_device_id') || generateDeviceId();
      localStorage.setItem('lemaitremot_device_id', deviceId);

      const response = await axios.post(`${API}/auth/register-free`, {
        email: registerEmail,
        password: registerPassword,
        password_confirm: registerPasswordConfirm
      }, {
        headers: {
          'X-Device-ID': deviceId
        },
        withCredentials: true
      });

      onAuthSuccess(response.data.session_token, registerEmail, false);

    } catch (error) {
      console.error('Error in register-free:', error);
      const status = error.response?.status;
      // Le backend renvoie `detail` sous forme d'OBJET ({error, message}) :
      // le passer tel quel à setRegisterError faisait planter le rendu React
      // (« Objects are not valid as a React child ») → écran blanc.
      const errorMsg = getApiErrorMessage(error, "Erreur lors de l'inscription");

      if (status === 409) {
        setRegisterError("Un compte existe déjà avec cet email. Connectez-vous plutôt.");
      } else if (status === 400) {
        setRegisterError(errorMsg);
      } else {
        setRegisterError("Erreur lors de l'inscription. Veuillez réessayer.");
      }
    } finally {
      setRegisterLoading(false);
    }
  };

  // =====================================================
  // LOGIN PASSWORD
  // =====================================================
  const handlePasswordLogin = async () => {
    if (!loginEmail || !loginPassword) return;

    setLoginLoading(true);
    try {
      const deviceId = localStorage.getItem('lemaitremot_device_id') || generateDeviceId();
      localStorage.setItem('lemaitremot_device_id', deviceId);

      const response = await axios.post(`${API}/auth/login-password`, {
        email: loginEmail,
        password: loginPassword
      }, {
        headers: {
          'X-Device-ID': deviceId
        },
        withCredentials: true
      });

      const isPro = response.data.is_pro === true;
      onAuthSuccess(response.data.session_token, loginEmail, isPro);

    } catch (error) {
      console.error('Error in password login:', error);
      const status = error.response?.status;
      const errorMsg = getApiErrorMessage(error, 'Erreur lors de la connexion');

      if (status === 400) {
        toast({
          title: "Mot de passe non défini",
          description: errorMsg,
          variant: "destructive"
        });
      } else if (status === 401) {
        toast({
          title: "Erreur de connexion",
          description: "Email ou mot de passe incorrect",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Erreur",
          description: errorMsg,
          variant: "destructive"
        });
      }
    } finally {
      setLoginLoading(false);
      setLoginPassword("");
    }
  };

  // =====================================================
  // CONNEXION PAR CODE — le parcours principal
  // =====================================================

  /**
   * Demande un code. Une panne d'envoi est dite comme telle.
   *
   * Le backend renvoie 503 quand l'envoi échoue vraiment, et il le peut sans
   * rien révéler : un code part que le compte existe ou non, donc un échec ne
   * s'explique jamais par l'absence de compte. C'est ce qui permet enfin de
   * distinguer « rien n'est parti » de « regardez vos spams ».
   */
  const demanderCode = async () => {
    const email = (codeEmail || "").trim();
    if (!email || codeLoading) return;
    setCodeLoading(true);
    setCodeErreur(null);
    try {
      await axios.post(`${API}/auth/request-code`, { email });
      setEtapeCode("code");
      setCodeSaisi("");
      toast({ title: "Code envoyé", description: `Vérifiez ${email}.` });
    } catch (error) {
      const statut = error.response?.status;
      setCodeErreur(
        statut === 422 ? "Cette adresse email ne semble pas valide."
        : statut === 429 ? "Trop de codes demandés. Réessayez dans quelques minutes."
        : statut === 503 ? "L'envoi du code a échoué. Réessayez dans un instant."
        : "La demande n'a pas abouti. Réessayez, ou utilisez votre mot de passe."
      );
    } finally {
      setCodeLoading(false);
    }
  };

  /**
   * Valide le code, puis connecte — ou crée le compte et connecte.
   *
   * `compte_cree` vient du serveur : à ce stade l'utilisateur a prouvé qu'il
   * contrôle l'adresse, donc l'information ne révèle rien et permet de dire
   * « votre compte est créé » plutôt que « content de vous revoir ».
   */
  const validerCode = async () => {
    // Le code est lu dans un mail où il est écrit « 339 530 » : on accepte
    // l'espace, et tout ce qui n'est pas un chiffre, plutôt que de refuser
    // une saisie fidèle à ce qu'on a envoyé.
    const code = (codeSaisi || "").replace(/\D/g, "");
    if (code.length < 4 || codeLoading) return;
    setCodeLoading(true);
    setCodeErreur(null);
    try {
      const r = await axios.post(`${API}/auth/verify-code`, {
        email: (codeEmail || "").trim(), code,
      });
      const cree = !!r.data.compte_cree;
      // Les évènements partent dans les deux cas — l'action initiale reprend
      // derrière l'écran de confirmation, elle ne l'attend pas.
      onAuthSuccess(r.data.session_token, r.data.email, r.data.is_pro, cree, cree);
      if (cree) setEtapeCode("cree");
    } catch (error) {
      const statut = error.response?.status;
      setCodeErreur(
        statut === 400 ? "Code invalide ou expiré. Demandez-en un nouveau."
        : statut === 429 ? "Trop de tentatives. Réessayez dans quelques minutes."
        : "La vérification n'a pas abouti. Réessayez dans un instant."
      );
      setCodeSaisi("");
    } finally {
      setCodeLoading(false);
    }
  };

  // =====================================================
  // LOGIN MAGIC LINK
  // =====================================================
  /**
   * UNE ERREUR RÉELLE NE DOIT PLUS PASSER POUR UN ENVOI RÉUSSI.
   *
   * Le `catch` affichait « Email envoyé » quoi qu'il arrive, y compris sur une
   * adresse rejetée ou une panne serveur. Mesuré en production : une adresse
   * invalide donnait 422 côté serveur et « Email envoyé ! Vérifiez vos spams »
   * côté écran. L'utilisateur partait attendre un courriel qu'aucun système
   * n'avait tenté d'envoyer.
   *
   * La protection contre l'énumération n'en souffre pas, et c'est le point
   * important : le serveur répond 200 QUE LE COMPTE EXISTE OU NON. Les erreurs
   * qu'on laisse remonter ici sont donc de transport ou de validation — elles
   * ne disent rien de l'existence d'un compte. Le message de succès, lui,
   * reste rigoureusement neutre.
   */
  const requestLogin = async (email) => {
    if (!email) return;

    setLoginLoading(true);
    setDevMagicLink(null);
    setLoginMagicError(null);
    try {
      const response = await axios.post(`${API}/auth/request-login`, {
        email: email
      });

      setLoginEmailSent(true);

      // P0: Afficher le lien magique en mode dev
      if (response.data.dev_mode && response.data.magic_link) {
        setDevMagicLink(response.data.magic_link);
        toast({
          title: "Lien magique (mode dev)",
          description: "Le lien est affiché ci-dessous pour copier.",
        });
      } else {
        toast({
          title: "Demande envoyée",
          description: MESSAGE_LIEN_NEUTRE,
        });
      }

    } catch (error) {
      console.error('Error requesting login:', error);
      const statut = error.response?.status;
      const message =
        statut === 422 ? "Cette adresse email ne semble pas valide. Vérifiez-la et réessayez."
        : statut === 429 ? "Trop de demandes en peu de temps. Réessayez dans quelques minutes."
        : "L'envoi n'a pas pu aboutir. Réessayez dans un instant, ou connectez-vous avec votre mot de passe.";
      // On NE passe PAS à l'écran « email envoyé » : le formulaire reste là,
      // avec l'erreur, pour que le geste puisse être repris.
      setLoginMagicError(message);
      toast({ title: "Demande non envoyée", description: message, variant: "destructive" });
    } finally {
      setLoginLoading(false);
    }
  };

  // =====================================================
  // RENDER: MODE REGISTER
  // =====================================================
  const renderRegisterMode = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="register-email">Adresse email</Label>
        <Input
          id="register-email"
          type="email"
          placeholder="votre@email.fr"
          value={registerEmail}
          onChange={(e) => setRegisterEmail(e.target.value)}
          disabled={registerLoading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="register-password">Mot de passe</Label>
        <Input
          id="register-password"
          type="password"
          placeholder={`${MIN_PASSWORD_LENGTH} caractères minimum`}
          value={registerPassword}
          onChange={(e) => setRegisterPassword(e.target.value)}
          disabled={registerLoading}
          aria-describedby="register-password-hint"
          minLength={MIN_PASSWORD_LENGTH}
        />
        <p id="register-password-hint" className="text-xs text-gray-500">
          {MIN_PASSWORD_LENGTH} caractères minimum.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="register-password-confirm">Confirmer le mot de passe</Label>
        <Input
          id="register-password-confirm"
          type="password"
          placeholder="Répétez le mot de passe"
          value={registerPasswordConfirm}
          onChange={(e) => setRegisterPasswordConfirm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && registerEmail && registerPassword && registerPasswordConfirm && !registerLoading) {
              handleRegisterFree();
            }
          }}
          disabled={registerLoading}
        />
      </div>

      {registerError && (
        <Alert variant="destructive">
          <AlertDescription>{registerError}</AlertDescription>
        </Alert>
      )}

      <InfoConfidentialite />

      <Button
        onClick={handleRegisterFree}
        disabled={!registerEmail || !registerPassword || !registerPasswordConfirm || registerLoading}
        className="w-full bg-green-600 hover:bg-green-700"
      >
        {registerLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Création en cours...
          </>
        ) : (
          <>
            <UserPlus className="mr-2 h-4 w-4" />
            Créer mon compte gratuit
          </>
        )}
      </Button>

      <div className="bg-green-50 p-3 rounded-lg text-xs text-green-700">
        <CheckCircle className="h-4 w-4 inline mr-1" />
        <strong>Compte gratuit :</strong> 3 exports PDF/jour, preview illimitée
      </div>

      <div className="text-center pt-2 border-t">
        <p className="text-xs text-gray-500 mb-2">
          Déjà un compte ?
        </p>
        <Button
          variant="link"
          onClick={() => setMode("login")}
          className="text-primary p-0 h-auto"
        >
          Se connecter
        </Button>
      </div>

      <div className="text-center">
        <Button
          variant="link"
          onClick={() => {
            closeLogin();
            navigate('/pricing');
          }}
          className="text-purple-600 p-0 h-auto text-xs"
        >
          <Crown className="h-3 w-3 mr-1" />
          {beta.active ? "Découvrir la bêta fondateur" : "Découvrir l'offre Pro"}
        </Button>
      </div>
    </div>
  );

  // =====================================================
  // RENDER: MODE LOGIN
  // =====================================================
  /* UN SEUL PARCOURS VISIBLE, ET UN RECOURS DISCRET.
   *
   * Trois onglets posaient à l'utilisateur une question à laquelle il ne peut
   * pas répondre : « préférez-vous un code, un mot de passe ou un lien
   * magique ? ». Un enseignant qui veut enregistrer sa fiche n'a pas d'opinion
   * là-dessus — il en formera une, mal, et une partie se trompera. Ce n'était
   * pas trois possibilités offertes, c'était une décision imposée avant la
   * première action utile.
   *
   * Le code reste seul en vue. Le mot de passe devient un écran secondaire,
   * atteint par un lien : il sert les comptes anciens, dont les gestionnaires
   * remplissent plus vite que n'importe quel code, et il est le seul recours
   * si l'envoi d'e-mails tombe. C'est une roue de secours, pas une option.
   *
   * Le lien magique quitte l'interface. Il prouvait exactement la même chose
   * que le code — le contrôle d'une boîte mail — pour un aller-retour hors du
   * produit, et c'est lui qui a fait perdre deux fois l'action en cours. Son
   * backend reste en place : le filet d'intention persistée aussi, tant qu'on
   * n'a pas démontré qu'il ne sert plus. */
  const renderLoginMode = () => (
    <div className="w-full">

      {/* ---------- Compte créé ---------- */}
      {etapeCode === "cree" ? (
        <div className="space-y-5 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent">
            <UserPlus className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h3 className="mb-1 font-semibold text-foreground">Votre compte gratuit est prêt</h3>
            <p className="text-sm text-muted-foreground">
              Vous êtes connecté avec {codeEmail}.
            </p>
          </div>
          {/* Aucune proposition de mot de passe ici. Le compte a déjà une
              méthode de connexion qui marche ; suggérer qu'il manque quelque
              chose serait faux, et un mot de passe créé dans la hâte est un
              mauvais mot de passe. Il vit dans les réglages du compte. */}
          <Button className="h-11 w-full" onClick={closeLogin}>Continuer</Button>
        </div>

      /* ---------- Écran secondaire : mot de passe ---------- */
      ) : ecranAuth === "motdepasse" ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="login-email-password">Adresse email</Label>
            <Input id="login-email-password" type="email" placeholder="votre@email.fr"
              className="h-11" value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && loginEmail && loginPassword && !loginLoading) handlePasswordLogin();
              }} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-password">Mot de passe</Label>
            <Input id="login-password" type="password" placeholder="••••••••"
              className="h-11" value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && loginEmail && loginPassword && !loginLoading) handlePasswordLogin();
              }} />
          </div>

          <Button onClick={handlePasswordLogin}
            disabled={!loginEmail || !loginPassword || loginLoading} className="h-11 w-full">
            {loginLoading
              ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Connexion…</>)
              : (<><LogIn className="mr-2 h-4 w-4" />Se connecter</>)}
          </Button>

          <div className="space-y-2 text-center">
            <Button variant="link" disabled={resetRequestLoading}
              onClick={handleResetPasswordRequest} className="h-11 p-0 text-sm text-muted-foreground">
              {resetRequestLoading ? "Envoi en cours…" : "Mot de passe oublié ?"}
            </Button>
            {resetRequestMessage && (
              <p className="rounded border border-green-200 bg-green-50 p-2 text-xs text-green-700">
                {resetRequestMessage}
              </p>
            )}
            {devResetLink && (
              <p className="break-all rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                Mode développement —{" "}
                <a href={devResetLink} className="font-medium underline">ouvrir le lien</a>
              </p>
            )}
          </div>

          {/* Le retour, toujours offert : le code reste le chemin normal. */}
          <div className="border-t pt-3 text-center">
            <Button variant="link" className="h-11 p-0 text-sm text-muted-foreground"
              onClick={() => { setEcranAuth("code"); setEtapeCode("email"); setCodeErreur(null); }}>
              Recevoir plutôt un code par email
            </Button>
          </div>
        </div>

      /* ---------- Écran principal : le code ---------- */
      ) : etapeCode === "email" ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="code-email">Adresse email</Label>
            <Input id="code-email" type="email" inputMode="email" autoComplete="email"
              placeholder="votre@email.fr" className="h-11" value={codeEmail}
              onChange={(e) => setCodeEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") demanderCode(); }} />
          </div>

          {/* Dit AVANT l'envoi. On ne peut pas savoir si l'adresse est connue
              sans le révéler : la phrase couvre les deux cas sans en trahir
              aucun, et personne ne découvre après coup qu'un compte a été créé
              en son nom. */}
          <p className="text-xs text-muted-foreground">
            Vous recevrez un code à six chiffres. Si vous n'avez pas encore de
            compte, il sera créé gratuitement.
          </p>

          {codeErreur && (
            <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[13px] text-destructive">
              {codeErreur}
            </p>
          )}

          <InfoConfidentialite />

          <Button onClick={demanderCode} disabled={!codeEmail || codeLoading} className="h-11 w-full">
            {codeLoading
              ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Envoi…</>)
              : (<><Mail className="mr-2 h-4 w-4" />Continuer</>)}
          </Button>

          <div className="border-t pt-3 text-center">
            <Button variant="link" className="h-11 p-0 text-sm text-muted-foreground"
              onClick={() => { setEcranAuth("motdepasse"); setCodeErreur(null); }}>
              J'ai déjà un mot de passe
            </Button>
          </div>
        </div>

      /* ---------- Écran principal : la saisie du code ---------- */
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="code-saisi">Code reçu par email</Label>
            <Input id="code-saisi" inputMode="numeric" autoComplete="one-time-code"
              placeholder="339 530" maxLength={9} autoFocus
              className="h-14 text-center font-mono text-2xl tracking-[0.4em]"
              value={codeSaisi}
              onChange={(e) => setCodeSaisi(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") validerCode(); }} />
            <p className="text-xs text-muted-foreground">
              Envoyé à {codeEmail}. Valable 10 minutes.
            </p>
          </div>

          {codeErreur && (
            <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[13px] text-destructive">
              {codeErreur}
            </p>
          )}

          <Button onClick={validerCode}
            disabled={codeSaisi.replace(/\D/g, "").length < 4 || codeLoading}
            className="h-11 w-full">
            {codeLoading
              ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Vérification…</>)
              : "Se connecter"}
          </Button>

          <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
            <Button variant="ghost" className="h-11 text-[13px]"
              onClick={() => { setEtapeCode("email"); setCodeErreur(null); }}>
              Changer d'adresse
            </Button>
            <Button variant="ghost" className="h-11 text-[13px]"
              onClick={demanderCode} disabled={codeLoading}>
              Renvoyer un code
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
      <DialogContent className="sm:max-w-md">
        {/* UN SEUL ÉCRAN, QUEL QUE SOIT LE POINT D'ENTRÉE.
            « Enregistrer » ouvrait « Connexion », « Exporter » ouvrait « Créer
            un compte » : même besoin, deux écrans, et l'utilisateur devait
            deviner dans quel cas il se trouvait. Le code répond aux deux —
            il connecte si le compte existe, le crée sinon — donc la question
            n'a plus lieu d'être posée. `mode` ne pilote plus le rendu ; il
            reste lu par le contexte pour la compatibilité des appelants. */}
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <LogIn className="mr-2 h-6 w-6 text-primary" />
            Connexion
          </DialogTitle>
          <DialogDescription>
            Entrez votre adresse email : vous recevrez un code à six chiffres.
          </DialogDescription>
        </DialogHeader>

        {renderLoginMode()}
      </DialogContent>
    </Dialog>
  );
}

export default GlobalLoginModal;
